import React, { useRef, useState } from 'react';
import { MessageSquare, Loader2, X, Send } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';
import MacroPicker from '@/components/admin/crm/MacroPicker';
import { insertMacroInto, restoreCaret } from '@/lib/crmMacros';

const MAX_CHARS = 918; // Twilio single-message limit; longer splits into segments

export default function BookingSmsComposer({ booking, onClose }) {
  const { toast } = useToast();
  const [message, setMessage] = useState(
    `Hi ${booking.customer_name?.split(' ')[0] || 'there'}, this is Motion Reality regarding your sim session. `,
  );
  const [sending, setSending] = useState(false);
  const messageRef = useRef(null);

  const insertMacro = (text) => {
    const { next, caret } = insertMacroInto(messageRef.current, text);
    setMessage(next.slice(0, MAX_CHARS));
    restoreCaret(messageRef.current, caret);
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await base44.functions.invoke('sendBookingSms', {
        to: booking.customer_phone,
        message: message.trim(),
      });
      if (res.data?.success) {
        toast({ title: 'SMS sent', description: `Delivered to ${booking.customer_phone}` });
        onClose();
      } else {
        throw new Error(res.data?.error || 'Send failed');
      }
    } catch (e) {
      toast({ title: 'Could not send SMS', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-primary/40 bg-[#161616] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">TEXT CUSTOMER</p>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">TO</p>
        <p className="text-white/60 text-xs break-all">{booking.customer_phone || '—'}</p>
      </div>

      <div className="space-y-2">
        <label className="block text-white/30 text-[10px] font-bold tracking-widest">MESSAGE</label>
        <MacroPicker
          channel="sms"
          context={{ customer_name: booking.customer_name, booking_ref: booking.id }}
          onInsert={insertMacro}
        />
        <textarea
          ref={messageRef}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
          rows={4}
          className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 resize-y focus:outline-none focus:border-primary/60"
        />
        <p className="text-right text-white/30 text-[10px] mt-1">{message.length} / {MAX_CHARS}</p>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-4 py-2 transition-colors"
        >
          CANCEL
        </button>
        <button
          onClick={handleSend}
          disabled={sending || !message.trim()}
          className="bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-5 py-2.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sending ? 'SENDING…' : 'SEND SMS'}
        </button>
      </div>
    </div>
  );
}